// src/pages/Pricing.jsx
import React, { useEffect, useRef, useMemo, useState } from "react";
import { useMe } from "../hooks/useMe";
import { getDisplayPrices } from "../config/pricing";
import Footer from "../components/Footer";
import { getStoredAttribution } from "../lib/attribution";
import { resolveCheckoutSource, persistCheckoutSource } from "../lib/checkoutSource";
import { trackEvent } from "../lib/analytics";

export default function Pricing({ onClose, lang = "is", theme = "dark", t, me }) {
  const isLight = theme === "light";
  const styles = getStyles(isLight);
  const prices = getDisplayPrices(lang);

  // If route doesn't pass `me`, fetch it here so Pricing can still gate buttons.
  const { me: hookMe } = useMe();
  const ME = me || hookMe;

  // Pull email from querystring if present (nice UX)
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const qsEmail = (params.get("email") || "").trim();

  // IMPORTANT: don't rely on userId for "logged in" checks (can be null temporarily).
  // Email presence is a safer signal for "we know who you are".
  const meEmail = (ME?.user?.email || "").trim();
  const email = qsEmail || meEmail; // querystring wins

  const proActive = !!ME?.entitlements?.pro;
  const proUntil = ME?.entitlements?.proUntil || null;

  const plan = ME?.subscription?.plan || null; // "monthly" | "yearly" | "unknown" | null
  const isYearly = proActive && plan === "yearly";
  const isMonthly = proActive && plan === "monthly";

  const [busyPlan, setBusyPlan] = useState(""); // "monthly" | "yearly" | ""
  const [error, setError] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  useEffect(() => {
    const qr = params.get("qr");
    if (qr) sessionStorage.setItem("qr_source", qr);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const viewFiredRef = useRef(false);
  useEffect(() => {
    if (viewFiredRef.current) return;
    viewFiredRef.current = true;
    trackEvent("pricing_page_viewed", {
      source: params.get("src") || "direct",
      lang,
      isPro: proActive,
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Translation helper (fallbacks only matter if a key is missing)
  const T = (key, fallback) => {
    if (typeof t === "function") {
      const v = t(key);
      return v == null ? fallback : v;
    }
    return fallback;
  };

  // --- Helpers --------------------------------------------------------------

  async function postJson(url, body) {
    const res = await fetch(url, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body || {}),
    });

    const text = await res.text();
    let json = null;
    try {
      json = text ? JSON.parse(text) : null;
    } catch {
      // non-JSON
    }

    return { res, json, text };
  }

  function pickErrorMessage({ res, json, text, fallback }) {
    return (
      json?.error ||
      json?.message ||
      (typeof text === "string" && text.trim() ? text.trim() : null) ||
      fallback ||
      `Request failed (${res?.status || "?"})`
    );
  }

  // If user is not logged in, route to /subscribe (email prefill) first.
  function goSubscribe(chosenPlan, source) {
    const to =
      `/subscribe?plan=${encodeURIComponent(chosenPlan)}` +
      (email ? `&email=${encodeURIComponent(email)}` : "") +
      (source ? `&src=${encodeURIComponent(source)}` : "");
    window.location.assign(to);
  }

  async function startCheckout(chosenPlan) {
    const source = resolveCheckoutSource();
    persistCheckoutSource(source);

    trackEvent("subscription_cta_clicked", {
      plan: chosenPlan,
      billingCycle: chosenPlan,
      source,
      lang,
    });

    if (busyPlan) return;
    if (!acceptedTerms) return;

    // If we don't have an email (not logged in / no identity), go through Subscribe.
    if (!email) {
      goSubscribe(chosenPlan, source);
      return;
    }

    const isUpgrade = isMonthly && chosenPlan === "yearly";
    if (isUpgrade) {
      trackEvent("upgrade_started", {
        fromTier: "monthly",
        toTier: "yearly",
        billingCycle: "yearly",
      });
    }

    setBusyPlan(chosenPlan);
    setError("");

    try {
      // Pricing is plan selector; if logged in, /api/checkout uses session.
      // We send only { plan } (backend ignores email anyway).
      const attribution = getStoredAttribution();
      const qrSource = sessionStorage.getItem("qr_source") || null;
      const { res, json, text } = await postJson("/api/checkout", {
        plan: chosenPlan,
        ...(attribution ? { attribution } : {}),
        ...(qrSource ? { qr_source: qrSource } : {}),
        ...(source ? { upgrade_source: source } : {}),
      });

      // Subscription already active / upgrade rules
      if (res.status === 409) {
        if (json?.code === "SUB_ACTIVE_YEARLY") {
          const until = json?.proUntil ? new Date(json.proUntil).toLocaleDateString() : "";
          throw new Error(
            T(
              "pricingAlreadyYearly",
              until
                ? `Your yearly subscription is already active until ${until}.`
                : "Your yearly subscription is already active."
            )
          );
        }

        if (json?.code === "SUB_ACTIVE_MONTHLY") {
          const until = json?.proUntil ? new Date(json.proUntil).toLocaleDateString() : "";
          throw new Error(
            T(
              "pricingMonthlyActive",
              until
                ? `You already have an active monthly subscription until ${until}.`
                : "You already have an active monthly subscription."
            )
          );
        }

        throw new Error(
          pickErrorMessage({
            res,
            json,
            text,
            fallback: T("subscribeSomethingWentWrong", "Something went wrong."),
          })
        );
      }

      if (!res.ok || !json?.ok) {
        if (res.status === 401) {
          // Session mismatch; send user through email-prefill flow.
          goSubscribe(chosenPlan, source);
          return;
        }

        throw new Error(
          pickErrorMessage({
            res,
            json,
            text,
            fallback: T("subscribeSomethingWentWrong", "Something went wrong."),
          })
        );
      }

      // ✅ Monthly -> Yearly upgrade can return { ok:true, upgraded:true } (no checkout URL)
      if (json?.upgraded) {
        trackEvent("upgrade_completed", {
          fromTier: "monthly",
          toTier: "yearly",
          billingCycle: "yearly",
        });
        window.location.assign("/?checkout=success&upgrade=1");
        return;
      }

      // Normal checkout needs a URL
      if (!json?.url) {
        throw new Error(T("subscribeMissingCheckoutUrl", "Missing checkout URL."));
      }

      trackEvent("checkout_started", {
        plan: chosenPlan,
        billingCycle: chosenPlan,
        priceIdType: "paddle",
        source,
      });
      window.location.assign(json.url);
    } catch (e) {
      setError(String(e?.message || e));
    } finally {
      setBusyPlan("");
    }
  }

  // Display-only prices (billing is still EUR in Paddle)
  const yearlyPrice = prices.yearly; // "€24.99" or "3.590 kr"
  const monthlyPrice = prices.monthly; // "€4.99"  or "790 kr"
  const pass30Price = prices.pass30; // "€6.99"  or "995 kr"
  const passYearPrice = prices.passyear; // "€29.99" or "4.268 kr"

  // Ticket #431: the Northern Lights bullet is removed from every plan's
  // feature list — it is no longer a Pro-exclusive benefit (full access is
  // open to every tier for the "northern_lights_free_v1" experiment), so
  // listing it here would be a false "pay for this" claim.
  const featuresYearly = [
    T("pricingFeatureComparisons", "Compare all 242 campsites across Iceland"),
    T("pricingFeatureAllPro", "All Pro features unlocked"),
    T("pricingFeatureWindShelter", "Wind direction + shelter score"),
    T("pricingFeatureCancelAnytime", "Cancel anytime"),
  ];

  const featuresMonthly = [
    T("pricingFeatureComparisons", "Compare all 242 campsites across Iceland"),
    T("pricingFeatureAllPro", "All Pro features unlocked"),
    T("pricingFeatureWindShelter", "Wind direction + shelter score"),
    T("pricingFeatureCancelAnytime", "Cancel anytime"),
  ];

  const featuresPass30 = [
    T("pricingFeatureComparisons", "Compare all 242 campsites across Iceland"),
    T("pricingFeatureAllPro", "All Pro features unlocked"),
    T("pricingFeatureWindShelter", "Wind direction + shelter score"),
  ];

  const featuresPassYear = [
    T("pricingFeatureComparisons", "Compare all 242 campsites across Iceland"),
    T("pricingFeatureAllPro", "All Pro features unlocked"),
    T("pricingFeatureWindShelter", "Wind direction + shelter score"),
  ];

  return (
    <div style={styles.page}>
      <style>{`
        .pricing-plans-grid {
          display: grid;
          gap: 12px;
          grid-template-columns: 1fr;
          margin-top: 16px;
        }
        @media (min-width: 500px) {
          .pricing-plans-grid { grid-template-columns: 1fr 1fr; }
        }
        @media (min-width: 1100px) {
          .pricing-plans-grid { grid-template-columns: repeat(4, minmax(0, 1fr)); }
        }
      `}</style>
      <div style={styles.glowTop} />
      <div style={styles.glowBottom} />

      <div style={styles.container}>
        {/* Back button */}
        <div style={styles.topBar}>
          <button
            type="button"
            style={styles.back}
            onClick={() => (onClose ? onClose() : window.history.back())}
          >
            {T("pricingBack", "← Back")}
          </button>
        </div>

        {/* Brand — centered above pricing card */}
        <div style={styles.brandCenter}>
          <div style={styles.brandHero} title={T("pricingBrandTitle", "CampCast Pro")}>
            <img
              src={!isLight ? "/eltumvedrid-dark-is.png" : "/eltumvedrid-light-is.png"}
              alt="Eltum Veðrið"
              style={styles.brandHeroLogo}
            />

          </div>
        </div>

        {/* Main card */}
        <div style={styles.card}>
          <h1 style={styles.h1}>{T("pricingTitle", "Choose your plan")}</h1>
          <p style={styles.p}>
            {T("pricingLead", "Pick monthly or yearly. Payment is handled securely by Paddle.")}
          </p>

          <div style={{ ...styles.finePrint, opacity: 0.5 }}>
            {T(
              "pricingChargedInEur",
              "Ath: Greitt er í evrum (€) í checkout. Bankinn umbreytir í ISK."
            )}
          </div>

          {!email ? (
            <div style={styles.note}>
              {T("pricingNotLoggedIn", "We’ll ask for your email before checkout.")}
            </div>
          ) : null}

          {isYearly ? (
            <div style={styles.infoBox}>
              <div style={{ fontWeight: 900, marginBottom: 4 }}>
                {T("pricingYearlyActive", "Subscription active")}
              </div>
              <div>
                {T("pricingAlreadyYearly", "Your yearly subscription is already active.")}
                {proUntil ? (
                  <>
                    <br />
                    {T("pricingActiveUntil", "Active until")}{" "}
                    {new Date(proUntil).toLocaleDateString()}
                  </>
                ) : null}
              </div>
            </div>
          ) : null}

          {isMonthly ? (
            <div style={styles.note}>
              <div style={{ fontWeight: 900, marginBottom: 4 }}>
                {T("pricingMonthlyActive", "Monthly subscription active")}
              </div>
              <div>
                {T(
                  "pricingMonthlyUpgradeHint",
                  "You can upgrade to yearly any time. Monthly cannot be purchased twice."
                )}
              </div>
            </div>
          ) : null}

          {error ? (
            <div style={styles.errorBox}>
              <div style={{ fontWeight: 900, marginBottom: 4 }}>
                {T("subscribeOopsTitle", "Oops!")}
              </div>
              <div>{error}</div>
            </div>
          ) : null}

          <label style={styles.termsRow}>
            <input
              type="checkbox"
              checked={acceptedTerms}
              onChange={(e) => setAcceptedTerms(e.target.checked)}
              style={styles.termsCheckbox}
            />
            <span>
              {T("termsPrivacyAgreePrefix", "I agree to the")}{" "}
              <a href="/terms" target="_blank" rel="noreferrer" style={styles.termsLink}>
                {T("termsDisclaimerLink", "Terms and Disclaimer")}
              </a>{" "}
              {T("termsPrivacyAgreeAnd", "and")}{" "}
              <a href="/privacy" target="_blank" rel="noreferrer" style={styles.termsLink}>
                {T("privacyLinkLabel", "Privacy Policy")}
              </a>
            </span>
          </label>

          <div className="pricing-plans-grid" style={styles.plansBase}>
            {/* 30-day travel pass (featured, popular badge) */}
            <div style={styles.planFeatured}>
              <div style={{ marginBottom: 10 }}>
                <span style={styles.badgePopular}>{T("pricingPassPopularBadge", "Most popular for travellers")}</span>
              </div>
              <div style={styles.planTitle}>{T("pricingPass30Title", "30-Day Travel Pass")}</div>
              <div style={styles.planPriceRow}>
                <div style={styles.planPrice}>{pass30Price}</div>
                <div style={styles.planPer}>{T("pricingPerOneTime", "one-time")}</div>
              </div>
              <div style={styles.planMicro}>
                {T("pricingPassMicro", "One-time purchase · No auto-renewal")}
              </div>
              <div style={{ fontSize: 12, opacity: 0.8, marginTop: 4, fontWeight: 700 }}>
                {T("pricingPass30Duration", "30 days from purchase date")}
              </div>
              <div style={styles.renewalText}>
                {T("pricingPass30RenewalText", "One-time purchase. Pro access for 30 days from the date of purchase. No automatic renewal.")}
              </div>

              <ul style={styles.featureList}>
                {featuresPass30.map((x) => (
                  <li key={x} style={styles.featureItem}>
                    <span aria-hidden>✅</span>
                    <span>{x}</span>
                  </li>
                ))}
              </ul>

              <div style={{ marginTop: "auto" }}>
                <button
                  type="button"
                  data-plan="pass30"
                  style={styles.cta(true, busyPlan === "pass30", !acceptedTerms || !!busyPlan)}
                  onClick={() => startCheckout("pass30")}
                  disabled={!acceptedTerms || !!busyPlan}
                >
                  {busyPlan === "pass30"
                    ? T("subscribeCtaBusy", "Opening checkout…")
                    : T("pricingPass30CTA", "Buy 30-day pass")}
                </button>
                <div style={styles.ctaConfirm}>
                  {T("pricingPassCtaConfirm", "One-time purchase. No automatic renewal.")}
                </div>
              </div>
            </div>

            {/* Monthly subscription */}
            <div style={styles.plan}>
              <div style={styles.planTitle}>{T("pricingMonthlyTitle", "Monthly")}</div>

              <div style={styles.planPriceRow}>
                <div style={styles.planPrice}>{monthlyPrice}</div>
                <div style={styles.planPer}>{T("pricingPerMonth", "per month")}</div>
              </div>

              <div style={styles.renewalText}>
                {T("pricingMonthlyRenewalText", "Renews automatically every month until cancelled. You can cancel at any time and keep access until the end of the paid period.")}
              </div>

              <ul style={styles.featureList}>
                {featuresMonthly.map((x) => (
                  <li key={x} style={styles.featureItem}>
                    <span aria-hidden>✅</span>
                    <span>{x}</span>
                  </li>
                ))}
              </ul>

              <div style={{ marginTop: "auto" }}>
                <button
                  type="button"
                  data-plan="monthly"
                  style={styles.cta(
                    false,
                    busyPlan === "monthly",
                    !acceptedTerms || !!busyPlan || isMonthly || isYearly
                  )}
                  onClick={() => startCheckout("monthly")}
                  disabled={!acceptedTerms || !!busyPlan || isMonthly || isYearly}
                >
                  {busyPlan === "monthly"
                    ? T("subscribeCtaBusy", "Opening checkout…")
                    : isMonthly
                      ? T("pricingMonthlyAlreadyActive", "Monthly is active")
                      : isYearly
                        ? T("pricingMonthlyNotAvailable", "Not available")
                        : T("pricingCtaMonthly", "Start monthly subscription")}
                </button>
                <div style={styles.ctaConfirm}>
                  {T("pricingCtaConfirm", "By continuing, you agree that your subscription will renew automatically according to the selected billing period until cancelled.")}
                </div>
              </div>
            </div>

            {/* Annual pass (one-time) */}
            <div style={styles.plan}>
              <div style={styles.planTitle}>{T("pricingPassYearTitle", "Annual Pass")}</div>

              <div style={styles.planPriceRow}>
                <div style={styles.planPrice}>{passYearPrice}</div>
                <div style={styles.planPer}>{T("pricingPerOneTime", "one-time")}</div>
              </div>

              <div style={styles.planMicro}>
                {T("pricingPassMicro", "One-time purchase · No auto-renewal")}
              </div>
              <div style={{ fontSize: 12, opacity: 0.8, marginTop: 4, fontWeight: 700 }}>
                {T("pricingPassYearDuration", "1 year (365 days) from purchase date")}
              </div>
              <div style={styles.renewalText}>
                {T("pricingPassYearRenewalText", "One-time purchase. Pro access for 1 year (365 days) from the date of purchase. No automatic renewal.")}
              </div>

              <ul style={styles.featureList}>
                {featuresPassYear.map((x) => (
                  <li key={x} style={styles.featureItem}>
                    <span aria-hidden>✅</span>
                    <span>{x}</span>
                  </li>
                ))}
              </ul>

              <div style={{ marginTop: "auto" }}>
                <button
                  type="button"
                  data-plan="passyear"
                  style={styles.cta(false, busyPlan === "passyear", !acceptedTerms || !!busyPlan)}
                  onClick={() => startCheckout("passyear")}
                  disabled={!acceptedTerms || !!busyPlan}
                >
                  {busyPlan === "passyear"
                    ? T("subscribeCtaBusy", "Opening checkout…")
                    : T("pricingPassYearCTA", "Buy annual pass")}
                </button>
                <div style={styles.ctaConfirm}>
                  {T("pricingPassCtaConfirm", "One-time purchase. No automatic renewal.")}
                </div>
              </div>
            </div>

            {/* Yearly subscription (featured, best value) */}
            <div style={styles.planFeatured}>
              <div style={{ marginBottom: 10 }}>
                <span style={styles.badgeBestValue}>{T("pricingBestValue", "Best value")}</span>
              </div>
              <div style={styles.planTitle}>{T("pricingYearlyTitle", "Yearly")}</div>
              <div style={styles.planPriceRow}>
                <div style={styles.planPrice}>{yearlyPrice}</div>
                <div style={styles.planPer}>{T("pricingPerYear", "per year")}</div>
              </div>
              <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
                {T("pricingYearlyEquivalentMonthly", "≈ €2 per month")}
              </div>
              <div style={{ fontSize: 12, color: "#059669", marginTop: 2 }}>
                {T("pricingYearlySavings", "Sparar um 5.890 kr á ári miðað við mánaðaráskrift")}
              </div>
              <div style={styles.planMicro}>
                {T("pricingYearlyMicro", "Billed once a year · Cancel anytime")}
              </div>
              <div style={styles.renewalText}>
                {T("pricingYearlyRenewalText", "Renews automatically every year until cancelled. You can cancel at any time and keep access until the end of the paid period.")}
              </div>

              <ul style={styles.featureList}>
                {featuresYearly.map((x) => (
                  <li key={x} style={styles.featureItem}>
                    <span aria-hidden>✅</span>
                    <span>{x}</span>
                  </li>
                ))}
              </ul>

              <div style={{ marginTop: "auto" }}>
                <button
                  type="button"
                  data-plan="yearly"
                  style={styles.cta(
                    true,
                    busyPlan === "yearly",
                    !acceptedTerms || !!busyPlan || isYearly
                  )}
                  onClick={() => startCheckout("yearly")}
                  disabled={!acceptedTerms || !!busyPlan || isYearly}
                >
                  {busyPlan === "yearly"
                    ? T("subscribeCtaBusy", "Opening checkout…")
                    : isMonthly
                      ? T("pricingCtaUpgradeToYearly", "Upgrade to Yearly")
                      : T("pricingCtaYearly", "Start annual subscription")}
                </button>
                <div style={styles.ctaConfirm}>
                  {T("pricingCtaConfirm", "By continuing, you agree that your subscription will renew automatically according to the selected billing period until cancelled.")}
                </div>
              </div>
            </div>
          </div>

          <div style={styles.finePrint}>
            {T("pricingFinePrint", "You can manage or cancel anytime in the billing portal.")}
          </div>

          {/* Ticket #431: the Pro-exclusive Aurora bullet is gone (above),
              but this note (same assessment, same detail for every tier) and
              its link to the detailed PricingInfo explanation remain genuinely
              informative on their own. */}
          <div style={{ ...styles.finePrint, marginTop: 6 }}>
            {T(
              "auroraInfoSameAssessment",
              "Free and Pro use the same nightly Northern Lights assessment, with the same level of detail and comparison."
            )}{" "}
            <a href="/pricing-info" style={styles.termsLink}>
              {T("pricingAuroraLearnMoreLink", "Learn more about Northern Lights")}
            </a>
          </div>
        </div>
      </div>
      <div style={{ maxWidth: 860, margin: "0 auto", padding: "0 16px" }}>
        <Footer t={t} />
      </div>
    </div>
  );
}

function getStyles(isLight) {
  const border = isLight ? "1px solid rgba(2,6,23,0.12)" : "1px solid rgba(255,255,255,0.12)";
  const softBorder = isLight ? "1px solid rgba(2,6,23,0.10)" : "1px solid rgba(255,255,255,0.10)";

  return {
    page: {
      minHeight: "100vh",
      padding: "28px 16px",
      color: isLight ? "#0B1220" : "white",
      background: isLight
        ? "radial-gradient(1200px 700px at 20% 10%, rgba(16,185,129,0.14), transparent 55%), radial-gradient(900px 600px at 85% 80%, rgba(59,130,246,0.10), transparent 55%), linear-gradient(180deg, #F7FAFC 0%, #EEF2F7 100%)"
        : "radial-gradient(1200px 700px at 20% 10%, rgba(16,185,129,0.18), transparent 55%), radial-gradient(900px 600px at 85% 80%, rgba(59,130,246,0.12), transparent 55%), linear-gradient(180deg, #060A12 0%, #060A12 100%)",
      position: "relative",
      overflow: "hidden",
    },

    glowTop: {
      position: "absolute",
      inset: "-200px -200px auto -200px",
      height: 420,
      background: "radial-gradient(circle at 30% 50%, rgba(16,185,129,0.22), transparent 60%)",
      filter: "blur(12px)",
      pointerEvents: "none",
      opacity: isLight ? 0.45 : 1,
    },
    glowBottom: {
      position: "absolute",
      inset: "auto -200px -220px -200px",
      height: 480,
      background: "radial-gradient(circle at 70% 40%, rgba(59,130,246,0.18), transparent 62%)",
      filter: "blur(12px)",
      pointerEvents: "none",
      opacity: isLight ? 0.4 : 1,
    },

    container: { maxWidth: 1200, margin: "0 auto", position: "relative" },

    topBar: {
      marginBottom: 0,
    },

    brandCenter: {
      display: "flex",
      justifyContent: "center",
      marginBottom: 24,
    },

    back: {
      border: 0,
      background: "transparent",
      color: isLight ? "#0B1220" : "rgba(255,255,255,0.9)",
      fontWeight: 700,
      cursor: "pointer",
      padding: "4px 10px",
      borderRadius: 12,
    },

    brandHero: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },

    brandHeroLogo: {
      height: 56,
      width: "auto",
      objectFit: "contain",
    },

    card: {
      borderRadius: 24,
      padding: 18,
      border,
      background: isLight ? "rgba(255,255,255,0.85)" : "rgba(2,6,23,0.55)",
      backdropFilter: "blur(10px)",
      boxShadow: isLight ? "0 18px 60px rgba(2,6,23,0.10)" : "0 22px 70px rgba(0,0,0,0.42)",
    },

    h1: { margin: 0, fontSize: 28, fontWeight: 950, letterSpacing: -0.4 },
    p: { marginTop: 8, marginBottom: 0, fontSize: 14, opacity: 0.85, lineHeight: 1.5 },

    note: {
      marginTop: 12,
      padding: 12,
      borderRadius: 16,
      border: softBorder,
      background: isLight ? "rgba(2,6,23,0.03)" : "rgba(255,255,255,0.03)",
      fontSize: 13,
      fontWeight: 700,
      opacity: 0.9,
    },

    infoBox: {
      marginTop: 12,
      borderRadius: 16,
      padding: 12,
      border: "1px solid rgba(16,185,129,0.28)",
      background: isLight ? "rgba(16,185,129,0.08)" : "rgba(16,185,129,0.10)",
      color: isLight ? "#064e3b" : "rgba(255,255,255,0.95)",
      fontSize: 13,
      lineHeight: 1.4,
    },

    errorBox: {
      marginTop: 12,
      borderRadius: 16,
      padding: 12,
      border: "1px solid rgba(239,68,68,0.28)",
      background: "rgba(239,68,68,0.10)",
      color: isLight ? "#7f1d1d" : "rgba(255,255,255,0.95)",
      fontSize: 13,
      lineHeight: 1.4,
    },

    termsRow: {
      marginTop: 14,
      display: "flex",
      alignItems: "flex-start",
      gap: 10,
      fontSize: 12,
      lineHeight: 1.5,
      opacity: 0.9,
    },

    termsCheckbox: {
      marginTop: 2,
      width: 14,
      height: 14,
      flex: "0 0 auto",
    },

    termsLink: {
      textDecoration: "underline",
      textUnderlineOffset: 2,
      color: "inherit",
      fontWeight: 800,
    },

    plansBase: {},

    planFeatured: {
      borderRadius: 20,
      padding: 14,
      border: "1px solid rgba(16,185,129,0.35)",
      background: isLight ? "rgba(16,185,129,0.06)" : "rgba(16,185,129,0.10)",
      boxShadow: isLight ? "0 18px 50px rgba(16,185,129,0.10)" : "0 18px 60px rgba(0,0,0,0.30)",
      display: "flex",
      flexDirection: "column",
      minWidth: 0,
    },

    plan: {
      borderRadius: 20,
      padding: 14,
      border: softBorder,
      background: isLight ? "rgba(2,6,23,0.03)" : "rgba(255,255,255,0.03)",
      display: "flex",
      flexDirection: "column",
      minWidth: 0,
    },

    planTitle: { fontWeight: 950, fontSize: 14, marginBottom: 6 },

    planPriceRow: { display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" },
    planPrice: { fontSize: 34, fontWeight: 950, letterSpacing: -0.6 },
    planPer: { fontSize: 13, fontWeight: 800, opacity: 0.75 },

    planMicro: { marginTop: 6, fontSize: 12, fontWeight: 700, opacity: 0.78 },

    renewalText: { marginTop: 6, fontSize: 11, fontWeight: 600, opacity: 0.70, lineHeight: 1.5 },

    ctaConfirm: { marginTop: 8, fontSize: 11, opacity: 0.68, lineHeight: 1.45, textAlign: "center" },

    badgeBestValue: {
      fontSize: 12,
      fontWeight: 950,
      padding: "8px 10px",
      borderRadius: 999,
      background: isLight ? "rgba(16,185,129,0.18)" : "rgba(16,185,129,0.20)",
      border: "1px solid rgba(16,185,129,0.32)",
      whiteSpace: "nowrap",
      alignSelf: "flex-start",
    },

    badgePopular: {
      fontSize: 12,
      fontWeight: 950,
      padding: "8px 10px",
      borderRadius: 999,
      background: isLight ? "rgba(245,158,11,0.18)" : "rgba(245,158,11,0.22)",
      border: "1px solid rgba(245,158,11,0.38)",
      color: isLight ? "#92400e" : "rgba(253,230,138,0.95)",
      whiteSpace: "nowrap",
      alignSelf: "flex-start",
    },

    featureList: { margin: "12px 0 0", padding: 0, listStyle: "none", display: "grid", gap: 8 },

    featureItem: {
      display: "flex",
      gap: 10,
      alignItems: "flex-start",
      fontSize: 13,
      fontWeight: 700,
    },

    cta: (isFeatured, busy, disabled) => ({
      marginTop: 14,
      width: "100%",
      borderRadius: 18,
      border: 0,
      cursor: disabled ? "not-allowed" : busy ? "not-allowed" : "pointer",
      padding: "12px 14px",
      fontSize: 14,
      fontWeight: 950,
      background: disabled
        ? isFeatured
          ? "rgba(16,185,129,0.35)"
          : "rgba(59,130,246,0.35)"
        : isFeatured
          ? busy
            ? "rgba(16,185,129,0.55)"
            : "rgba(16,185,129,0.95)"
          : busy
            ? "rgba(59,130,246,0.55)"
            : "rgba(59,130,246,0.92)",
      color: "white",
      opacity: disabled ? 0.75 : 1,
      boxShadow: disabled
        ? "none"
        : isFeatured
          ? "0 12px 30px rgba(16,185,129,0.22)"
          : "0 12px 30px rgba(59,130,246,0.20)",
    }),

    finePrint: { marginTop: 12, fontSize: 12, opacity: 0.75, textAlign: "center" },
  };
}
